import { useState } from 'react';
import { Link } from 'react-router-dom';

import type { PlanSummary } from '@rheora/enrollment-contract';

import { useTranslation } from '@fluxora/i18n';
import { Badge, Button, Card, CardBody } from '@fluxora/ui';

import { useUsageSelection } from '../hooks/useUsageSelection';
import { usePlanDetailPrefetch } from '../api/plans.prefetch';
import { buildPlanDetailPath } from '../services/funnel-rehydrate.service';
import {
  centsAreZero,
  formatCentsToDollars,
  formatRenewablePct,
} from '../services/price.service';
import { PlanPriceHeadline } from './PlanPriceHeadline';

/**
 * A plan card for the meter-filtered shelf (SHOP-5A), priced at the shopper's
 * CHOSEN monthly usage rather than a fixed EFL level.
 *
 * Shares the landing `PlanCard`'s two-faced anatomy: the FRONT is for scanning
 * (name, price, chips), and the §25.475 withheld-price explainer lives on the
 * BACK behind the ⓘ. That paragraph used to sit on the front of every
 * promotional plan, which made those cards twice the height of their
 * neighbours and the whole shelf ragged — the disclosure is never dropped, it
 * moved one tap away.
 *
 * ── THE PRICE IS RENDERED BY `PlanPriceHeadline`, NOT HERE ─────────────────
 * This card used to fetch price points itself and format its own headline, which
 * made TWO components claim to be "the" price renderer — and they drifted: the
 * card interpolated + ROUNDED while `PlanPriceHeadline` truncated the disclosed
 * string, so the same plan could show a different figure on the shelf and on the
 * detail page it linked to. Now there is one price component and one calculator
 * (`headlineRateAt`), and this file cannot re-open that gap.
 *
 * ── THE "VIEW PLAN" LINK CARRIES THE USAGE ────────────────────────────────
 * The detail page must headline the SAME kWh the shopper is looking at here, and
 * survive a refresh once it gets there, so the link is built by
 * `buildPlanDetailPath` from the live selection — never assembled inline.
 */
export interface MeterPlanCardProps {
  readonly plan: PlanSummary;
  /**
   * The chosen monthly usage (kWh) to price at. A PROP rather than another read of
   * the funnel because `MeterPlansPage` has already guarded it non-`undefined` —
   * the card should not have to re-handle "no usage yet". The link additionally
   * needs the TIER, which only the funnel has, hence `useUsageSelection` below;
   * both come from the same `FunnelProvider` state, so they cannot disagree.
   */
  readonly monthlyKwh: number;
}

export function MeterPlanCard({ plan, monthlyKwh }: MeterPlanCardProps): JSX.Element {
  const { t } = useTranslation();
  const [flipped, setFlipped] = useState(false);
  // Warm the detail on the signals that precede a click; the card stays
  // presentational and the hook owns the cache.
  const prefetchDetail = usePlanDetailPrefetch();
  const selection = useUsageSelection();

  const detailPath = buildPlanDetailPath(plan.tariffId, selection);

  const chips = (
    <div className="flex flex-wrap justify-center gap-2">
      <Badge variant="default">
        {plan.termMonths > 0
          ? t('shop.card.term', { months: plan.termMonths })
          : t('shop.card.term.none')}
      </Badge>
      <Badge variant="success">
        {t('shop.card.renewable', { pct: formatRenewablePct(plan.recPctMin) })}
      </Badge>
      <Badge variant="default">
        {centsAreZero(plan.etfCents)
          ? t('shop.card.etf.none')
          : t('shop.card.etf', { amount: formatCentsToDollars(plan.etfCents) })}
      </Badge>
    </div>
  );

  return (
    <div className={`flip ${flipped ? 'flipped' : ''}`}>
      <div className="flip-inner">
        {/* ── FRONT: scan me ─────────────────────────────────────────────── */}
        <Card
          variant="glass"
          padding="md"
          className="flip-face hover-lift relative flex h-full flex-col justify-between gap-4 shadow-card"
        >
          <CardBody className="flex flex-col gap-3 p-0">
            {/* The flip control, floated to the corner — a real button with a
                spoken name; the "ⓘ" glyph alone is a mystery to a screen
                reader. */}
            <button
              type="button"
              aria-label={t('shop.card.info.aria')}
              title={t('shop.card.info.aria')}
              onClick={() => {
                setFlipped(true);
                prefetchDetail(plan.tariffId);
              }}
              className="absolute right-4 top-4 flex h-7 w-7 items-center justify-center rounded-full border border-border text-xs font-bold text-foreground/60 transition-colors hover:border-primary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              i
            </button>

            <div className="flex min-w-0 flex-col items-center gap-0.5 px-7 text-center">
              <span className="font-display w-full truncate text-lg font-bold tracking-tight text-foreground">
                {plan.planName}
              </span>
              <span className="text-sm text-foreground/60">
                {t('shop.card.provider', { name: plan.supplierName })}
              </span>
            </div>

            {/* Effective ¢/kWh at the chosen usage — the headline for this
                shelf. Compact: the withheld-price explainer is the back
                face's whole job. */}
            <PlanPriceHeadline planId={plan.tariffId} monthlyKwh={monthlyKwh} detail="compact" align="center" />

            {chips}
          </CardBody>

          <Link
            to={detailPath}
            className="inline-flex border-t border-border/60 pt-3"
            onMouseEnter={() => {
              prefetchDetail(plan.tariffId);
            }}
            onFocus={() => {
              prefetchDetail(plan.tariffId);
            }}
          >
            <Button variant="primary" shape="pill" fullWidth className="cta-sheen">
              {t('shop.card.viewDetail')}
            </Button>
          </Link>
        </Card>

        {/* ── BACK: read me ──────────────────────────────────────────────── */}
        <Card
          variant="glass"
          padding="md"
          className="flip-face flip-back flex h-full flex-col justify-between gap-4 shadow-card"
          aria-hidden={!flipped}
        >
          <CardBody className="flex flex-col gap-3 p-0">
            <span className="text-xs font-semibold uppercase tracking-wide text-foreground/50">
              {t('shop.card.aboutPrice')}
            </span>
            <span className="font-display text-lg font-bold tracking-tight text-foreground">
              {plan.planName}
            </span>
            {/* The FULL disclosure, including the withheld-price paragraph the
                front deliberately does not carry. */}
            <PlanPriceHeadline planId={plan.tariffId} monthlyKwh={monthlyKwh} detail="full" />
          </CardBody>
          <Button variant="secondary" shape="pill" fullWidth onClick={() => setFlipped(false)}>
            {t('shop.card.back')}
          </Button>
        </Card>
      </div>
    </div>
  );
}
